import arrayHandle from './baseLib/arrayHandle';
import { RtnType } from './declareTypes';
import { ValidationError } from "class-validator";

/**
 * Http异常
 */
class HttpException extends Error {
  status: number;
  message: string;
  errors?: any;
  constructor(status: number, message: string, errors?: any) {
    super(message);
    this.status = status;
    this.message = message;
    this.errors = errors;
  }
};

/**
 * 错误提示
 * @param errorCode 错误代码
 * @param message 错误信息
 * @param errors 错误详情
 */
export const throwError = (errorCode: number, message: string, errors?: ValidationError[]) => {
  throw new HttpException(errorCode, message, errors);
};

/** 错误类型详情 */
const errorTypeDetails = {
  /** 输入验证错误 */
  INPUTVALIDATIONERRORS: {
    /** id */
    id: 'INPUTVALIDATIONERRORS',
    /** 名称 */
    name: '输入验证错误',
    /** 详情 */
    details: '用户输入或请求数据不符合预期格式或规则。子类：必填字段缺失（REQUIRED_FIELD_MISSING）数据类型不匹配（INVALID_DATA_TYPE）数据格式错误（如邮箱、日期格式错误，INVALID_EMAIL_FORMAT）数值范围越界（VALUE_OUT_OF_RANGE）'
  },
  /** 业务逻辑错误 */
  BUSINESSLOGICERRORS: {
    /** id */
    id: 'BUSINESSLOGICERRORS',
    /** 名称 */
    name: '业务逻辑错误',
    /** 详情 */
    details: '违反业务规则或流程的操作。子类：状态冲突（如订单已支付时重复支付，INVALID_ORDER_STATUS）权限不足（PERMISSION_DENIED）资源不存在（如用户ID不存在，USER_NOT_FOUND）操作冲突（如并发修改冲突，CONCURRENT_MODIFICATION）'
  },
  /** 资源错误 */
  RESOURCEERRORS: {
    /** id */
    id: 'RESOURCEERRORS',
    /** 名称 */
    name: '资源错误',
    /** 详情 */
    details: '系统资源不足或访问失败。子类：内存不足（OUT_OF_MEMORY）磁盘空间不足（DISK_SPACE_FULL）数据库连接失败（DATABASE_CONNECTION_FAILED）文件读写失败（FILE_IO_ERROR）'
  },
  /** 网络与通信错误 */
  NETWORKANDCOMMUNICATIONERRORS: {
    /** id */
    id: 'NETWORKANDCOMMUNICATIONERRORS',
    /** 名称 */
    name: '网络与通信错误',
    /** 详情 */
    details: '网络请求或通信相关问题。子类：请求超时（REQUEST_TIMEOUT）连接中断（CONNECTION_RESET）API调用失败（API_CALL_FAILED）DNS解析失败（DNS_RESOLUTION_FAILED）'
  },
  /** 安全与认证错误 */
  SECURITYANDAUTHENTICATIONERRORS: {
    /** id */
    id: 'SECURITYANDAUTHENTICATIONERRORS',
    /** 名称 */
    name: '安全与认证错误',
    /** 详情 */
    details: '身份验证或数据安全问题。子类：无效凭证（INVALID_CREDENTIALS）会话过期（SESSION_EXPIRED）访问被拒绝（ACCESS_DENIED）加密/解密失败（ENCRYPTION_ERROR）'
  },
  /** 第三方服务错误 */
  THIRDPARTYSERVICEERRORS: {
    /** id */
    id: 'THIRDPARTYSERVICEERRORS',
    /** 名称 */
    name: '第三方服务错误',
    /** 详情 */
    details: '依赖的外部服务或组件异常。子类：服务不可用（SERVICE_UNAVAILABLE）API配额超限（API_RATE_LIMIT_EXCEEDED）数据同步失败（DATA_SYNC_FAILED）'
  },
  /** 配置错误 */
  CONFIGURATIONERRORS: {
    /** id */
    id: 'CONFIGURATIONERRORS',
    /** 名称 */
    name: '配置错误',
    /** 详情 */
    details: '系统配置不当导致的问题。子类：配置文件缺失（CONFIG_FILE_MISSING）参数未配置（MISSING_CONFIG_PARAMETER）环境变量错误（INVALID_ENV_VARIABLE）'
  },
  /** 客户端与服务器端错误 */
  HTTPCODEERRORS: {
    /** id */
    id: 'HTTPCODEERRORS',
    /** 名称 */
    name: '客户端与服务器端错误',
    /** 详情 */
    details: '客户端错误（4xx）：400 Bad Request（请求语法错误）401 Unauthorized（未授权）404 Not Found（资源不存在）服务端错误（5xx）：500 Internal Server Error（服务器内部错误）503 Service Unavailable（服务不可用）'
  },
  /** 兼容性错误 */
  COMPATIBILITYERRORS: {
    /** id */
    id: 'COMPATIBILITYERRORS',
    /** 名称 */
    name: '兼容性错误',
    /** 详情 */
    details: '版本或格式不兼容。子类：API版本不兼容（API_VERSION_MISMATCH）数据格式不支持（UNSUPPORTED_DATA_FORMAT）'
  },
  /** 性能错误 */
  PERFORMANCEERRORS: {
    /** id */
    id: 'PERFORMANCEERRORS',
    /** 名称 */
    name: '性能错误',
    /** 详情 */
    details: '系统性能相关问题。子类：响应超时（RESPONSE_TIMEOUT）高负载（HIGH_SYSTEM_LOAD）'
  },
  /** 其他错误 */
  OTHERERRORS: {
    /** id */
    id: 'OTHERERRORS',
    /** 名称 */
    name: '其他错误',
    /** 详情 */
    details: '子类：未知错误（UNKNOWN_ERROR）未处理的异常（UNHANDLED_EXCEPTION）'
  }
};

/** 错误代码类型 */
export declare type errorCodeIdType = '输入验证错误' | '设备未注册' | '设备禁止登录' | '需要设备id' | '需要设备码' |
  '未关联设备' | '设备关联已禁用' | '用户已禁用' | '用户登录已禁用' | '未知错误';

declare interface errorCodeListType {
  /** 错误id */
  id: errorCodeIdType,
  /** 错误代码 */
  code: number,
  /** 名称 */
  name?: string,
  /** 类型 */
  type?: any,
  /** 对策 */
  solution?: string
};

/** 错误代码列表 */
const errorCodeList: Array<errorCodeListType> = [
  {
    id: '输入验证错误',
    code: 601,
    name: '输入验证错误',
    type: errorTypeDetails['INPUTVALIDATIONERRORS'],
    solution: '检查输入'
  },
  {
    id: '设备未注册',
    code: 901,
    name: '设备未注册',
    type: errorTypeDetails['SECURITYANDAUTHENTICATIONERRORS'],
    solution: '注册设备'
  },
  {
    id: '设备禁止登录',
    code: 902,
    name: '设备禁止登录',
    type: errorTypeDetails['SECURITYANDAUTHENTICATIONERRORS'],
    solution: '充许设备登录'
  },
  {
    id: '需要设备id',
    code: 903,
    name: '需要设备id',
    type: errorTypeDetails['SECURITYANDAUTHENTICATIONERRORS'],
    solution: '注册设备或获取设备id'
  },
  {
    id: '需要设备码',
    code: 904,
    name: '需要设备码',
    type: errorTypeDetails['SECURITYANDAUTHENTICATIONERRORS'],
    solution: '输入设备码'
  },
  {
    id: '未关联设备',
    code: 905,
    name: '未关联设备',
    type: errorTypeDetails['SECURITYANDAUTHENTICATIONERRORS'],
    solution: '关联设备'
  },
  {
    id: '设备关联已禁用',
    code: 906,
    name: '设备关联已禁用',
    type: errorTypeDetails['SECURITYANDAUTHENTICATIONERRORS'],
    solution: '启用设备关联'
  },
  {
    id: '用户已禁用',
    code: 920,
    name: '用户已禁用',
    type: errorTypeDetails['SECURITYANDAUTHENTICATIONERRORS'],
    solution: '启用用户'
  },
  {
    id: '用户登录已禁用',
    code: 921,
    name: '用户登录已禁用',
    type: errorTypeDetails['SECURITYANDAUTHENTICATIONERRORS'],
    solution: '启用用户登录'
  }
];

/**
 * 获取错误代码
 * @param errorCodeId 错误代码id
 * @param options 选项
 */
export const getErrorCode = (errorCodeId: errorCodeIdType, options?: {
  /** 回传rtn数据 */
  rtn?: RtnType
}) => {
  const { rtn } = options || {};
  let res = {
    id: '未知错误',
    code: 999,
    name: '未知错误',
    type: errorTypeDetails['OTHERERRORS'],
    solution: '检查代码'
  }
  try {
    const findData = arrayHandle.findValue(errorCodeList, errorCodeId, {
      keyIdName: 'id'
    })
    if (findData.success === true) {
      const [data, _index] = findData.data;
      res = data;
    }
  }
  catch { }
  if (typeof rtn !== 'undefined') {
    rtn.success = false;
    rtn.errorCode = res.code;
    rtn.message = `${res.name}；对策: ${res.solution}；${typeof rtn.message !== undefined ? rtn.message : ''}`;
  }
  return res;
}

